import { RemoteDocKind, RemoteReference } from "@/api/accounting.tsx"
import { check_detail_path } from "@/accounting/check_path"

export const REMOTE_DOC_LABEL: Record<RemoteDocKind, string> = {
  credit_memo: "Credit memo",
  payment: "Payment",
  invoice: "Invoice",
  journal_entry: "Journal entry",
}

/** Where a document lives in MarginWiz, or null when nothing links it to a check or a deduction. */
export function remote_reference_path(reference: RemoteReference): string | null {
  if (reference.deduction_id) return `/deduction/${reference.deduction_id}`
  if (reference.check_number) return check_detail_path(reference.check_number)
  return null
}

/** Where a global-search row goes: its record, or the page that says it has none. */
export function remote_reference_link(reference: RemoteReference): string {
  return remote_reference_path(reference) ?? `/netsuite/${encodeURIComponent(reference.remote_id)}`
}

export type RemoteReferenceOutcome =
  | { kind: "go"; path: string }
  /** Several records, for a document that spans them or a number that is not unique. */
  | { kind: "choose"; references: RemoteReference[] }
  /** Synced from the accounting system, but nothing in MarginWiz links to it. */
  | { kind: "unlinked"; references: RemoteReference[] }
  | { kind: "none" }

/** What `/netsuite/<id>` does with what it found. */
export function remote_reference_outcome(references: RemoteReference[]): RemoteReferenceOutcome {
  if (references.length === 0) return { kind: "none" }
  const linked = references.filter((reference: RemoteReference) => remote_reference_path(reference) !== null)
  if (linked.length === 0) return { kind: "unlinked", references }
  const paths = new Set(linked.map((reference: RemoteReference) => remote_reference_path(reference)))
  if (paths.size === 1 && linked.length === references.length) {
    return { kind: "go", path: remote_reference_path(linked[0])! }
  }
  return { kind: "choose", references }
}

if (import.meta.vitest) {
  const { describe, expect, it } = import.meta.vitest

  function reference(overrides: Partial<RemoteReference>): RemoteReference {
    return {
      kind: "credit_memo",
      remote_id: "27572788",
      doc_number: "4005",
      customer_name: "31256 Target",
      date: "2026-07-31",
      amount: 658868.39,
      check_number: null,
      deduction_id: null,
      ...overrides,
    }
  }

  describe("remote_reference_outcome", () => {
    it("says plainly when nothing matches", () => {
      expect(remote_reference_outcome([])).toEqual({ kind: "none" })
    })

    it("goes straight to the one check a document belongs to", () => {
      const memo = reference({ check_number: "3324943" })
      expect(remote_reference_outcome([memo])).toEqual({ kind: "go", path: "/accounting/check/3324943" })
    })

    it("encodes a check number that would otherwise break the URL", () => {
      const memo = reference({ check_number: "ACH# 09-01-2026" })
      expect(remote_reference_outcome([memo])).toEqual({ kind: "go", path: "/accounting/check/ACH~23~2009-01-2026" })
    })

    it("goes to a deduction when that is the record", () => {
      const invoice = reference({ kind: "invoice", deduction_id: "abc" })
      expect(remote_reference_outcome([invoice])).toEqual({ kind: "go", path: "/deduction/abc" })
    })

    it("treats two documents on one check as one destination", () => {
      const memo = reference({ check_number: "3324943" })
      const payment = reference({ kind: "payment", remote_id: "29661491", check_number: "3324943" })
      expect(remote_reference_outcome([memo, payment])).toEqual({ kind: "go", path: "/accounting/check/3324943" })
    })

    it("offers a choice when the records differ", () => {
      const first = reference({ deduction_id: "abc" })
      const second = reference({ deduction_id: "def" })
      expect(remote_reference_outcome([first, second]).kind).toBe("choose")
    })

    it("does not drop an unlinked document that shares its number with a linked one", () => {
      const linked = reference({ check_number: "3324943" })
      const unlinked = reference({ kind: "invoice", remote_id: "9" })
      expect(remote_reference_outcome([linked, unlinked]).kind).toBe("choose")
    })

    it("reports a synced document nothing links to", () => {
      const memo = reference({})
      expect(remote_reference_outcome([memo])).toEqual({ kind: "unlinked", references: [memo] })
    })
  })

  describe("remote_reference_link", () => {
    it("sends an unlinked document to the page that explains it", () => {
      expect(remote_reference_link(reference({}))).toBe("/netsuite/27572788")
    })
  })
}
